import { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Text,
  Flex,
  IconButton,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';

import CustomerForm, {
  getEmptyCustomerFormData,
  isCustomerFormValid,
  type CustomerFormData,
} from './CustomerForm';
import { COMPANY_NAME } from '../constants';

interface AddCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CustomerFormData) => void;
  isSaving: boolean;
}

const AddCustomerModal = ({
  isOpen,
  onClose,
  onSave,
  isSaving,
}: AddCustomerModalProps) => {
  const [formData, setFormData] = useState<CustomerFormData>(
    getEmptyCustomerFormData(),
  );

  const handleClose = () => {
    setFormData(getEmptyCustomerFormData());
    onClose();
  };

  const handleSave = () => {
    onSave(formData);
  };

  const isValid = isCustomerFormValid(formData);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent overflow="hidden">
        <Flex
          bg="linear-gradient(135deg, #E8683A 0%, #F47B4A 100%)"
          px={5}
          py={4}
          justify="space-between"
          align="center"
        >
          <Text
            color="white"
            fontSize="sm"
            fontWeight="700"
            letterSpacing="wider"
            textTransform="uppercase"
          >
            Add New Customer
          </Text>
          <IconButton
            aria-label="Close"
            icon={<CloseIcon />}
            size="xs"
            variant="outline"
            color="white"
            borderColor="white"
            onClick={handleClose}
            _hover={{ bg: 'whiteAlpha.200' }}
          />
        </Flex>
        <Box bg="gray.200" py={3} px={5}>
          <Text
            color="gray.700"
            fontSize="sm"
            fontWeight="600"
            textAlign="center"
          >
            {COMPANY_NAME}
          </Text>
        </Box>
        <ModalBody maxH="60vh" overflowY="auto" pt={5}>
          <CustomerForm data={formData} onChange={setFormData} />
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="brand"
            onClick={handleSave}
            isLoading={isSaving}
            isDisabled={!isValid}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddCustomerModal;
